import { MealType } from '@akin-home/amplify';
import { filterHasValue } from '@akin-home/arrays';
import { formatDateString } from '@akin-home/dates';
import { Meal } from '@akin-home/meals';
import { Text } from '@akin-home/text/native';
import { colors } from '@akin-home/theme';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { isBefore } from 'date-fns';
import { useMemo } from 'react';
import { StyleProp, View, ViewStyle } from 'react-native';
import styled from 'styled-components/native';
import { NewMealPlanTile } from '../NewMealPlanTile';

interface Props {
  date: string;

  current_time: Date;

  meals: Record<MealType, Meal | undefined>;

  favourite_recipe_ids?: string[];

  style?: StyleProp<ViewStyle>;

  isToday?: boolean;

  onViewRecipe?(recipe_id: string): void;
  onSwapMeal?(meal: Meal): void;
  onPressEaten?(meal: Meal): void;
  onPressFavourite?(meal: Meal): void;
  onPressEmpty(): void;
}

export const MealPlanDayRow = (props: Props) => {
  const { date, current_time, meals, isToday, favourite_recipe_ids } = props;

  const meal_list = useMemo(
    () => filterHasValue(Object.values(meals)),
    [meals]
  );

  return (
    <View style={props.style}>
      <Header>
        <Text weight="semibold">{formatDateString(date, 'EEEE, d MMM')}</Text>

        {isToday ? (
          <TodayBadge>
            <FontAwesomeIcon color={colors.primary[90]} icon="star" size={16} />

            <Text
              variant="caption"
              weight="semibold"
              color={colors.primary[90]}
            >
              Today
            </Text>
          </TodayBadge>
        ) : null}
      </Header>

      {meal_list.length ? (
        <TilesContainer>
          {meal_list.map((meal) => (
            <Tile
              key={meal.id}
              meal={meal}
              isPast={isBefore(meal.start, current_time)}
              isFavourite={favourite_recipe_ids?.includes(meal.recipes[0]?.id)}
              onPressViewRecipe={props.onViewRecipe}
              onPressSwap={props.onSwapMeal}
              onPressEaten={props.onPressEaten}
              onPressFavourite={props.onPressFavourite}
            />
          ))}
        </TilesContainer>
      ) : (
        <EmptyTile onPress={props.onPressEmpty}>
          <Text weight="semibold">
            No Meals Planned. Press here to update your preferences
          </Text>
        </EmptyTile>
      )}
    </View>
  );
};

const TILE_HEIGHT = '303px';
const TILE_WIDTH = '290px';

const Header = styled.View`
  flex-direction: row;

  align-items: center;

  padding: 16px;
`;

const TilesContainer = styled.View`
  flex-direction: row;
  gap: 30px;
  padding: 16px;
`;

const Tile = styled(NewMealPlanTile)`
  height: ${TILE_HEIGHT};
  max-width: ${TILE_WIDTH};
  flex: 1;
  elevation: 3;
`;

const EmptyTile = styled.TouchableOpacity`
  height: ${TILE_HEIGHT};

  flex: 1;

  max-width: 100%;

  border-radius: 15px;
  background-color: rgba(231, 231, 231, 1);

  border-width: 2px;
  border-color: rgba(210, 210, 210, 1);
  border-style: dashed;

  align-items: center;
  justify-content: center;
  margin: 16px;
`;

const TodayBadge = styled.View`
  flex-direction: row;
  align-items: center;
  padding: 8px 12px;
  background-color: rgba(47, 57, 238, 0.1);
  border-radius: 20px;
  margin-left: 16px;
  gap: 8px;
`;
